import { usePrintedDashboard, usePrintedReport, usePrintedThread } from "@/api/queries";
import { Markdown } from "@/components/chat/Markdown";
import { DashboardDocument, PrintArtifact, ReportDocument } from "@/print/documents";
import { hasCharts, mapCount } from "@/print/tiles";
import { PrintLayout, SqlAppendix } from "@/print/PrintLayout";
import { usePrintReady } from "@/print/usePrintReady";

/**
 * The print routes (PRD FR-9.1, FR-9.2).
 *
 * **This page is the PDF.** The renderer navigates here with a session cookie,
 * waits for `window.__mingPrintReady`, and prints what it sees — so a chart in
 * a PDF is drawn by the same ECharts option builder that drew it on screen,
 * with the same folding into "Other" and the same `MING_LOCALE` formatting.
 * There is no second renderer to drift from the first, which is the same rule
 * a tile follows.
 *
 * It is a normal authenticated route. Nothing about it is exempt: no bypass on
 * the loopback address, no unauthenticated export path. What is different is
 * only the stylesheet and the absence of interactive furniture — `chrome=false`
 * on the cards, because a PDF with a "Copy" button drawn on it lies about what
 * a reader can do.
 */

export function PrintReportPage({ reportId, sql }: { reportId: string; sql: boolean }) {
  const { data: report } = usePrintedReport(reportId);
  const reportArtifacts = (report?.tiles ?? []).map((tile) => tile.artifact);
  usePrintReady(Boolean(report), hasCharts(reportArtifacts), mapCount(reportArtifacts));
  return report ? <ReportDocument report={report} sql={sql} /> : null;
}

export function PrintDashboardPage({
  dashboardId,
  sql,
}: {
  dashboardId: string;
  sql: boolean;
}) {
  const { data: dashboard } = usePrintedDashboard(dashboardId);
  const dashboardArtifacts = (dashboard?.tiles ?? []).map((tile) => tile.artifact);
  usePrintReady(Boolean(dashboard), hasCharts(dashboardArtifacts), mapCount(dashboardArtifacts));
  return dashboard ? <DashboardDocument dashboard={dashboard} sql={sql} /> : null;
}

export function PrintConversationPage({
  threadId,
  sql,
}: {
  threadId: string;
  sql: boolean;
}) {
  const { data: thread } = usePrintedThread(threadId);
  const messages = thread?.messages ?? [];
  const artifacts = messages.flatMap((message) => message.artifacts ?? []);
  usePrintReady(Boolean(thread), hasCharts(artifacts), mapCount(artifacts));

  if (!thread) return null;

  const queries = messages
    .flatMap((message) => message.steps ?? [])
    .filter((step) => step.tool === "execute_sql" && step.sql)
    .map((step, index) => ({
      index: index + 1,
      sql: step.sql ?? "",
      connection: thread.connection_name ?? undefined,
      rows: step.row_count,
    }));

  return (
    <PrintLayout
      title={thread.title || "Conversation"}
      asOf={thread.last_message_at}
      facts={[
        ["Source", thread.connection_name ?? ""],
        ["Turns", String(messages.filter((message) => message.role === "assistant").length)],
      ].filter((pair): pair is [string, string] => Boolean(pair[1]))}
    >
      {messages.map((message) =>
        message.role === "user" ? (
          <h2 key={message.id} className="type-h3 text-ink print-block mt-6 mb-2">
            {message.text}
          </h2>
        ) : (
          <div key={message.id} className="mb-4">
            {message.text ? (
              <div className="type-body text-ink print-block mb-3">
                <Markdown>{message.text}</Markdown>
              </div>
            ) : null}
            {(message.artifacts ?? []).map((artifact, index) => (
              <PrintArtifact key={`${message.id}-${index}`} artifact={artifact} />
            ))}
            {message.insights?.length ? (
              <ul className="type-support text-ink-secondary print-block list-disc pl-5">
                {message.insights.map((insight) => (
                  <li key={insight} className="mb-1">
                    {insight}
                  </li>
                ))}
              </ul>
            ) : null}
            {message.model_label ? (
              <p className="type-caption text-ink-disabled mt-1 normal-case">
                Answered by {message.model_label}
              </p>
            ) : null}
          </div>
        ),
      )}
      {sql ? <SqlAppendix queries={queries} /> : null}
    </PrintLayout>
  );
}
